import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaSearch, FaStore, FaMapMarkerAlt, FaPhoneAlt, FaEye, FaSyncAlt, FaTimes } from 'react-icons/fa';
import { useShops } from '../hooks/useShops';
import ShopDetailsModal from '../components/shops/ShopDetailsModal';
import AdsPanel from '../components/shops/AdsPanel';
import PackagesPanel from '../components/shops/PackagesPanel';
import BookingsPanel from '../components/shops/BookingsPanel';
import SpecialitiesPanel from '../components/shops/SpecialitiesPanel';
import { StatusChip, formatDate } from '../components/shops/shopConstants';
import '../styles/shops.scss';

const TABS = [
    { key: 'review', label: 'طلبات المراجعة' },
    { key: 'shops', label: 'الورش' },
    { key: 'ads', label: 'الإعلانات' },
    { key: 'bookings', label: 'الحجوزات' },
    { key: 'packages', label: 'باقات الإعلانات' },
    { key: 'specialities', label: 'التخصصات' },
];

const STATUS_FILTERS = [
    { value: '', label: 'الكل' },
    { value: 'approved', label: 'معتمدة' },
    { value: 'pending', label: 'قيد المراجعة' },
    { value: 'rejected', label: 'مرفوضة' },
    { value: 'suspended', label: 'موقوفة' },
];

const ShopCard = ({ shop, onOpen, review }) => {
    const isUpdate = shop.status === 'approved' && shop.pendingUpdate;
    const content = review && isUpdate ? shop.pendingUpdate : shop;
    return (
        <button type="button" className="shop-card" onClick={() => onOpen(shop._id)}>
            <div className="shop-card-top">
                {content.logo ? <img src={content.logo} alt="" /> : <div className="logo-placeholder"><FaStore /></div>}
                <div className="shop-card-title">
                    <strong>{content.name}</strong>
                    <span className="muted small">{shop.ownerId?.ownerName || '—'}</span>
                </div>
                {review ? (
                    <span className={`status-chip ${isUpdate ? 'tone-info' : 'tone-warning'}`}>{isUpdate ? 'تعديلات' : 'ورشة جديدة'}</span>
                ) : (
                    <StatusChip status={shop.status} />
                )}
            </div>
            <div className="shop-card-meta">
                <span><FaMapMarkerAlt /> {content.city}{content.cityExists === false && <em className="flag flag-missing">جديدة</em>}</span>
                <span><FaPhoneAlt /> <bdi>{content.phone}</bdi></span>
            </div>
            <div className="chips">
                {(content.specialities || []).slice(0, 4).map((s) => <span key={s._id || s} className="chip small">{s.name || s}</span>)}
                {(content.proposedSpecialities || []).map((name) => <span key={name} className="chip small chip-new">{name}</span>)}
            </div>
            <div className="shop-card-foot muted small">
                {review ? (
                    <>أُرسلت {formatDate(isUpdate ? shop.pendingUpdate.submittedAt : shop.updatedAt, true)}</>
                ) : (
                    <>
                        <span><FaEye /> {shop.stats?.views || 0} مشاهدة</span>
                        <span><FaPhoneAlt /> {shop.stats?.calls || 0} اتصال</span>
                        {shop.pendingUpdate && <span className="status-chip tone-info">تعديلات معلقة</span>}
                    </>
                )}
            </div>
        </button>
    );
};

export const Shops = () => {
    const actions = useShops();
    const { summary, specialities, cities, loadSummary } = actions;
    const [searchParams, setSearchParams] = useSearchParams();
    const ownerFilter = searchParams.get('owner') || '';
    const [tab, setTab] = useState(ownerFilter ? 'shops' : 'review');
    const [filters, setFilters] = useState({ q: '', status: '', city: '', specialityId: '' });
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ shops: [], total: 0, pageSize: 20 });
    const [loading, setLoading] = useState(false);
    const [openShopId, setOpenShopId] = useState(null);

    const loadShops = useCallback(async () => {
        if (tab !== 'review' && tab !== 'shops') return;
        setLoading(true);
        const query = tab === 'review'
            ? { view: 'review', page }
            : { ...filters, ownerId: ownerFilter, page };
        const json = await actions.listShops(query);
        if (json) setData(json);
        setLoading(false);
    }, [tab, filters, ownerFilter, page]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadShops(); }, [loadShops]);

    // Debounced name/phone search.
    useEffect(() => {
        const t = setTimeout(() => { setFilters((prev) => (prev.q === search ? prev : { ...prev, q: search })); setPage(1); }, 350);
        return () => clearTimeout(t);
    }, [search]);

    const reviewCount = (summary?.pendingShops || 0) + (summary?.pendingUpdates || 0);
    const badgeFor = (key) => {
        if (key === 'review') return reviewCount;
        if (key === 'ads') return summary?.pendingAds || 0;
        return 0;
    };
    const pages = Math.max(1, Math.ceil((data.total || 0) / (data.pageSize || 20)));

    const onChanged = () => { loadShops(); loadSummary(); };

    return (
        <section className="container shops">
            <div className="shops-header">
                <div>
                    <h1>الورش والإعلانات</h1>
                    <div className="header-stats">
                        <span><strong>{summary?.approvedShops ?? '—'}</strong> ورشة معتمدة</span>
                        <span><strong>{summary?.activeAds ?? '—'}</strong> إعلان نشط</span>
                        <span><strong>{summary?.specialities ?? '—'}</strong> تخصص</span>
                    </div>
                </div>
                <div className="tabs">
                    {TABS.map((t) => (
                        <button type="button" key={t.key} className={tab === t.key ? 'active' : ''} onClick={() => { setTab(t.key); setPage(1); }}>
                            {t.label}
                            {badgeFor(t.key) > 0 && <span className="badge">{badgeFor(t.key)}</span>}
                        </button>
                    ))}
                </div>
            </div>

            {(tab === 'review' || tab === 'shops') && (
                <div className="panel">
                    {tab === 'shops' && (
                        <div className="panel-toolbar filters">
                            <div className="search-box">
                                <FaSearch />
                                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث بالاسم أو الهاتف أو العنوان" />
                            </div>
                            <select value={filters.status} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}>
                                {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                            <select value={filters.city} onChange={(e) => { setFilters({ ...filters, city: e.target.value }); setPage(1); }}>
                                <option value="">كل المدن</option>
                                {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <select value={filters.specialityId} onChange={(e) => { setFilters({ ...filters, specialityId: e.target.value }); setPage(1); }}>
                                <option value="">كل التخصصات</option>
                                {specialities.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                            </select>
                            {ownerFilter && (
                                <button type="button" className="chip chip-accent removable" onClick={() => setSearchParams({})}>
                                    ورش مالك محدد <FaTimes />
                                </button>
                            )}
                            <button type="button" className="icon-btn" onClick={onChanged} aria-label="تحديث"><FaSyncAlt /></button>
                        </div>
                    )}
                    {tab === 'review' && (
                        <div className="panel-toolbar">
                            <div className="muted">ورش جديدة وتعديلات على ورش معتمدة بانتظار موافقتك. الأقدم أولاً.</div>
                            <button type="button" className="icon-btn" onClick={onChanged} aria-label="تحديث"><FaSyncAlt /></button>
                        </div>
                    )}

                    {loading ? (
                        <div className="loading">جاري التحميل...</div>
                    ) : data.shops.length === 0 ? (
                        <div className="empty-state">{tab === 'review' ? 'لا توجد طلبات بانتظار المراجعة 🎉' : 'لا توجد ورش مطابقة'}</div>
                    ) : (
                        <div className="shops-grid">
                            {data.shops.map((shop) => (
                                <ShopCard key={shop._id} shop={shop} review={tab === 'review'} onOpen={setOpenShopId} />
                            ))}
                        </div>
                    )}

                    {pages > 1 && (
                        <div className="pagination">
                            <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>السابق</button>
                            <span>{page} / {pages}</span>
                            <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)}>التالي</button>
                        </div>
                    )}
                </div>
            )}

            {tab === 'ads' && <AdsPanel actions={actions} summary={summary} />}
            {tab === 'bookings' && <BookingsPanel actions={actions} />}
            {tab === 'packages' && <PackagesPanel actions={actions} />}
            {tab === 'specialities' && <SpecialitiesPanel specialities={specialities} actions={actions} />}

            {openShopId && (
                <ShopDetailsModal
                    shopId={openShopId}
                    actions={actions}
                    specialities={specialities}
                    cities={cities}
                    onClose={() => setOpenShopId(null)}
                    onChanged={onChanged}
                />
            )}
        </section>
    );
};
